function sleep (ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function ua () {
  return String(navigator.userAgent || '')
}

function looksLikeCoinbaseProvider (p) {
  if (!p) return false
  return !!(
    p.isCoinbaseWallet ||
    p.isCoinbaseBrowser ||
    p.isCoinbaseWalletExtension ||
    p.isWalletLink ||
    p.overrideIsMetaMask ||
    p.isSmartWallet ||
    p._isSmartWallet ||
    p.signerType === 'smartWallet' ||
    p.connectionType === 'smartWallet'
  )
}

export function isCoinbaseWalletEnv () {
  const agent = ua()
  if (/CoinbaseWallet|CBWallet|CoinbaseBrowser|\bCBW\b|Coinbase/i.test(agent)) return true
  if (typeof window !== 'undefined' && (window.coinbaseWalletExtension || window.__COINBASE_WALLET__)) return true
  const p = window.ethereum
  if (!p) return false
  if (looksLikeCoinbaseProvider(p)) return true
  if (p.providerMap?.get?.('CoinbaseWallet')) return true
  if (Array.isArray(p.providers) && p.providers.some(looksLikeCoinbaseProvider)) return true
  return false
}

function isSmartWalletProvider (p) {
  if (!p) return false
  return !!(p.isSmartWallet || p._isSmartWallet || p.signerType === 'smartWallet' || p.connectionType === 'smartWallet')
}

function collectInjectedProviders () {
  const list = []
  const eth = window.ethereum
  if (Array.isArray(eth?.providers)) list.push(...eth.providers)
  else if (eth) list.push(eth)
  if (window.coinbaseWalletExtension) list.push(window.coinbaseWalletExtension)
  return list.filter(Boolean)
}

export function getEthereumProvider () {
  const list = collectInjectedProviders()
  if (!list.length) return null

  const coinbaseEoa = list.find(p => looksLikeCoinbaseProvider(p) && !isSmartWalletProvider(p))
  if (coinbaseEoa) return coinbaseEoa

  if (isCoinbaseWalletEnv()) {
    const anyCb = list.find(p => looksLikeCoinbaseProvider(p) && !isSmartWalletProvider(p))
    if (anyCb) return anyCb
    return list.find(p => !isSmartWalletProvider(p)) || list[0]
  }

  const metamask = list.find(p => p.isMetaMask && !looksLikeCoinbaseProvider(p) && !p.isBraveWallet)
  return metamask || window.ethereum
}

function readAddressFromProvider (provider) {
  const candidates = [
    provider?.selectedAddress,
    provider?.address,
    provider?._state?.accounts?.[0],
    provider?.accounts?.[0],
    provider?._addresses?.[0],
    window.ethereum?.selectedAddress
  ]
  const addr = candidates.find(v => /^0x[a-fA-F0-9]{40}$/.test(String(v || '')))
  return addr || ''
}

function chainType (chainId) {
  const n = parseInt(chainId, 16)
  if (n === 56 || n === 97 || n === 137 || n === 80001) return 2
  return 0
}

async function readAccounts (provider) {
  try {
    const accounts = await provider.request({ method: 'eth_accounts' })
    if (accounts?.[0]) return accounts[0]
  } catch (_) { /* */ }
  try {
    if (typeof provider.send === 'function') {
      const result = provider.send({ method: 'eth_accounts' })
      const accounts = typeof result?.then === 'function' ? await result : result?.result || result
      if (accounts?.[0]) return accounts[0]
    }
  } catch (_) { /* */ }
  return readAddressFromProvider(provider)
}

function waitForAccountsChanged (provider, timeoutMs) {
  return new Promise(resolve => {
    if (!provider?.on) {
      resolve('')
      return
    }
    const timer = setTimeout(() => {
      provider.removeListener?.('accountsChanged', onChange)
      resolve('')
    }, timeoutMs)
    const onChange = (accounts) => {
      clearTimeout(timer)
      provider.removeListener?.('accountsChanged', onChange)
      resolve(accounts?.[0] || '')
    }
    provider.on('accountsChanged', onChange)
  })
}

/**
 * Coinbase 内置浏览器里调用 eth_requestAccounts / enable()
 * 会被当成 Base Smart Wallet 开通交易。
 * 先静默读地址；只有非 Coinbase 才 requestAccounts。
 */
export async function connectEvmWallet () {
  const provider = getEthereumProvider()
  if (!provider) return null

  const coinbase = isCoinbaseWalletEnv()
  const retries = coinbase ? 12 : 3
  const gap = coinbase ? 250 : 150

  let address = readAddressFromProvider(provider)
  if (!address) {
    for (let i = 0; i < retries; i++) {
      address = await readAccounts(provider)
      if (address) break
      if (i < retries - 1) await sleep(gap)
    }
  }

  if (!address && coinbase) {
    address = await Promise.race([
      waitForAccountsChanged(provider, 1500),
      sleep(1500).then(() => '')
    ])
    if (!address) address = await readAccounts(provider)
  }

  // Coinbase：绝不调用 enable / eth_requestAccounts，避免 Base 开通费弹窗
  if (!address && !coinbase) {
    const accounts = await provider.request({ method: 'eth_requestAccounts' })
    address = accounts?.[0] || ''
  }

  if (!address) return null

  let addressType = 0
  try {
    const chainId = await provider.request({ method: 'eth_chainId' })
    addressType = chainType(chainId)
  } catch (_) { /* */ }

  return { address, addressType, provider }
}
